"use client";

import Link from 'next/link';
import { Check, ArrowRight, ShieldCheck, MousePointerClick, Zap } from 'lucide-react';
import Layout from '@/components/Layout';
import NicheTag from '@/components/NicheTag';
import { templates } from '@/data/siteData';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import usePageTitle from '@/hooks/usePageTitle';
import JsonLd from '@/components/SEO/JsonLd';
import LemonSqueezyScript from '@/components/checkout/LemonSqueezyScript';

const templatesFAQ = [
  { q: 'Do I need to know how to code?', a: 'Basic familiarity with HTML helps, but our templates come with detailed documentation. For a truly no-code experience, consider our custom build packages.' },
  { q: 'Can I customize the colors and fonts?', a: 'Absolutely. Every template uses CSS custom properties and Tailwind config, making it easy to update your brand colors and typography in one place.' },
  { q: "What's included in the download?", a: 'Complete source code, documentation, sample content, accessibility audit report, and deployment guide for Vercel or Netlify.' },
  { q: 'Do templates include ongoing updates?', a: 'Templates include 6 months of updates. After that, you can purchase extended support or upgrade to a Care Plan.' },
  { q: 'Need something more custom?', a: 'Our custom packages start at $750. Send us a message via our contact page and we\'ll find the right fit for your organization.' },
];

export default function TemplatesPage() {
  usePageTitle('Website Templates — Laurel Web Co.');

  const templatesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Website Templates",
    "numberOfItems": templates.length,
    "itemListElement": templates.map((t, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": t.name,
        "description": `Accessibility-first website template for ${t.niche}s. Features: ${t.features.join(', ')}`,
        "offers": {
          "@type": "Offer",
          "price": t.price,
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
        }
      }
    }))
  };

  return (
    <Layout>
      <JsonLd schema={templatesSchema} />
      <section 
        className="bg-bloom-green-deep min-h-[40vh] pt-40 pb-24 flex items-center"
        aria-label="Templates cover"
      >
        <div className="container mx-auto px-6">
          <h1 className="heading-hero text-white">Website Templates</h1>
          <p className="font-sans text-lg text-white/90 mt-6 max-w-xl leading-relaxed">
            Accessibility-first website templates built for nonprofits, churches, and community organizations.
          </p>
        </div>
      </section>

      <section className="bg-card py-20" aria-label="Template benefits">
        <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { icon: <ShieldCheck className="w-8 h-8 text-bloom-sage" aria-hidden="true" />, title: 'WCAG 2.1 AA', desc: 'Every template is accessibility-ready out of the box so that no retrofit is needed.' },
            { icon: <MousePointerClick className="w-8 h-8 text-bloom-sage" aria-hidden="true" />, title: 'No-code editing', desc: 'Swap text, images, and colors in minutes with zero developer time.' },
            { icon: <Zap className="w-8 h-8 text-bloom-sage" aria-hidden="true" />, title: 'Instant delivery', desc: 'Get your template files the moment you purchase and launch your site the same day.' },
          ].map((v) => (
            <div key={v.title} className="flex flex-col items-center text-center bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="mb-4 flex items-center justify-center w-14 h-14 rounded-full bg-bloom-sage/10">
                {v.icon}
              </div>
              <h3 className="font-serif text-lg font-bold text-bloom-charcoal mb-2">{v.title}</h3>
              <p className="font-sans text-sm text-bloom-muted leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-bloom-cream py-24" aria-labelledby="templates-grid-heading">
        <div className="container mx-auto px-6">
          <h2 id="templates-grid-heading" className="sr-only">Available Templates</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {templates.map((t) => (
              <div key={t.slug} className="bg-card rounded-2xl overflow-hidden shadow-sm border border-bloom-border flex flex-col">
                <div className="aspect-[4/3] bg-muted relative flex items-center justify-center text-center p-8">
                  <span className="font-serif italic text-bloom-muted/30" aria-hidden="true">{t.name} Preview</span>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-serif text-xl font-bold text-bloom-charcoal mb-2">{t.name}</h3>
                  <div className="flex items-center gap-2 mb-4">
                    <NicheTag niche={t.niche} />
                    <span className="font-sans font-semibold text-bloom-charcoal">${t.price}</span>
                  </div>
                  <ul className="space-y-2 mb-6 flex-1" role="list">
                    {t.features.map((f) => (
                      <li key={f} className="flex gap-2 font-sans text-sm text-bloom-charcoal">
                        <Check size={14} className="text-bloom-green-light flex-shrink-0 mt-0.5" aria-hidden="true" />{f}
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-3">
                    <Link 
                      href={`/templates/${t.slug}`} 
                      className="flex-1 text-center px-4 py-2 rounded-lg border border-bloom-border text-bloom-charcoal font-sans text-sm font-medium hover:bg-muted transition-colors focus-bloom"
                      aria-label={`Preview the ${t.name} template`}
                    >
                      Preview
                    </Link>
                    <a 
                      href={t.checkoutUrl || '#'}
                      className={`flex-1 px-4 py-2 rounded-lg bg-bloom-green-deep text-white font-sans text-sm font-medium hover:bg-bloom-green-mid transition-colors focus-bloom text-center ${t.checkoutUrl ? 'lemonsqueezy-button' : 'opacity-50 cursor-not-allowed'}`}
                      aria-label={`Buy the ${t.name} template for $${t.price}`}
                    >
                      Buy
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-card rounded-2xl p-8 border-2 border-bloom-gold text-center shadow-lg" role="region" aria-labelledby="bundle-heading">
            <span className="inline-block px-3 py-1 rounded-full bg-bloom-gold text-white font-sans text-xs font-medium mb-3">Best value</span>
            <h3 id="bundle-heading" className="font-serif text-2xl font-bold text-bloom-charcoal">Complete 3-Template Bundle for $497</h3>
            <p className="font-sans text-sm text-bloom-muted mt-2">Save over $190 by purchasing the complete mission collection.</p>
            <a 
              href="https://laurelwebco.lemonsqueezy.com/checkout/buy/aeaadb7b-21ef-4b9d-83f6-cf68ff114482?embed=1" 
              className="mt-6 px-8 py-3 rounded-lg bg-bloom-gold text-white font-sans font-semibold hover:bg-bloom-gold-dark transition-colors focus-bloom inline-block lemonsqueezy-button"
              aria-label="Buy the Complete Template Bundle for $497"
            >
              Purchase the Bundle for $497
            </a>
          </div>
        </div>
      </section>

      <section className="bg-card py-24" aria-labelledby="faq-heading">
        <div className="container mx-auto px-6 max-w-2xl">
          <h2 id="faq-heading" className="heading-section text-bloom-charcoal mb-12 text-center">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {templatesFAQ.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-bloom-cream rounded-xl border border-bloom-border px-6">
                <AccordionTrigger className="font-sans font-medium text-bloom-charcoal text-left hover:no-underline">{faq.q}</AccordionTrigger>
                <AccordionContent className="font-sans text-sm text-bloom-muted leading-relaxed">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="bg-bloom-green-deep py-20" aria-label="Consultation call to action">
        <div className="container mx-auto px-6 text-center">
          <h2 className="heading-card text-white mb-4">Need something built for you instead?</h2>
          <Link 
            href="/services" 
            className="font-sans text-white/90 hover:text-white underline focus-bloom rounded inline-flex items-center gap-1"
            aria-label="View our custom web development services"
          >
            View Custom Services <ArrowRight size={14} aria-hidden="true" />
          </Link>
        </div>
      </section>
      <LemonSqueezyScript />
    </Layout>
  );
}
